import React, { useState, useEffect } from 'react';
import './Chatbot.css';
import ChatbotAvatar from './ChatbotAvatar';
import ChatbotModal from './ChatbotModal';
import ChatInterface from './ChatInterface';
import VoiceInterface from './VoiceInterface';
import { useAppDispatch } from '../../store';
import { clearChatData } from '../../store/slices/chatSlice';

// Dynamic map URL function
const getMapUrl = () => {
  // Check if running in embed mode (iframe)
  const embedCity = (window as any).__VIEWPRO_CITY__;
  
  // Check URL params (for standalone mode)
  const urlParams = new URLSearchParams(window.location.search);
  const urlAubreyCity = urlParams.has('aubrey-tx') || urlParams.get('community') === 'aubrey-tx';
  const urlBaytownCity = urlParams.has('baytown-tx') || urlParams.get('community') === 'baytown-tx';
  
  // Use embed city first, then fall back to URL params
  const isAubreyTx = embedCity === 'aubrey-tx' || urlAubreyCity;
  const isBaytownTx = embedCity === 'baytown-tx' || urlBaytownCity;
  
  if (isAubreyTx) {
    return 'https://map.viewprogis.com/vp/aubrey-tx';
  }
  
  if (isBaytownTx) {
    return 'https://map.viewprogis.com/ecp/baytownqc-tx';
  }
  
  // Default map URL
  return 'https://map.viewprogis.com/demo/ai-demo?widget=0fbad9e5-18ef-4901-93a4-1103882f9c66';
};

interface ChatbotProps {
  onStateChange?: (isOpen: boolean) => void;
}

const Chatbot: React.FC<ChatbotProps> = ({ onStateChange }) => {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showChatInterface, setShowChatInterface] = useState(false);
  const [showVoiceInterface, setShowVoiceInterface] = useState(false);

  const handleAvatarClick = () => {
    setShowModal(true);
    // Notify parent component
    if (onStateChange) onStateChange(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Notify parent component
    if (onStateChange) onStateChange(false);
    // Ensure chat data is cleared in Redux store
    dispatch(clearChatData());
  };

  const handleOpenChat = () => {
    setShowModal(false);
    setShowChatInterface(true);
    // Notify parent component - still open, just different interface
    if (onStateChange) onStateChange(true);
  };

  const handleCloseChat = () => {
    setShowChatInterface(false);
    // Notify parent component
    if (onStateChange) onStateChange(false);
  };

  const handleOpenVoice = () => {
    setShowModal(false);
    setShowVoiceInterface(true);
    // Notify parent component - still open, just different interface
    if (onStateChange) onStateChange(true);
  };

  const handleCloseVoice = () => {
    setShowVoiceInterface(false);
    // Notify parent component
    if (onStateChange) onStateChange(false);
  };

  // Listen for external control events from parent window
  useEffect(() => {
    const handleControlEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const action = customEvent.detail?.action;
      
      console.log('[CHATBOT] Received control event:', action);
      
      if (action === 'open') {
        // Open the modal (welcome screen)
        setShowModal(true);
        if (onStateChange) onStateChange(true);
      } else if (action === 'close') {
        // Close everything
        setShowModal(false);
        setShowChatInterface(false);
        setShowVoiceInterface(false);
        if (onStateChange) onStateChange(false);
        dispatch(clearChatData());
      } else if (action === 'toggle') {
        // Toggle the modal state
        const isCurrentlyOpen = showModal || showChatInterface || showVoiceInterface;
        if (isCurrentlyOpen) {
          setShowModal(false);
          setShowChatInterface(false);
          setShowVoiceInterface(false);
          if (onStateChange) onStateChange(false);
          dispatch(clearChatData());
        } else {
          setShowModal(true);
          if (onStateChange) onStateChange(true);
        }
      }
    };
    
    window.addEventListener('chatbot-control', handleControlEvent as EventListener);
    
    return () => {
      window.removeEventListener('chatbot-control', handleControlEvent as EventListener);
    };
  }, [showModal, showChatInterface, showVoiceInterface, onStateChange, dispatch]);

  return (
    <div className="chatbot-wrapper no-blur">
      <ChatbotAvatar 
        showInitialMessage={true} 
        messageDelay={2000}
        onClick={handleAvatarClick}
      />
      
      {showModal && (
        <ChatbotModal 
          isOpen={true} 
          onClose={handleCloseModal}
          onChatClick={handleOpenChat}
          onVoiceClick={handleOpenVoice}
          onMapClick={() => window.open(getMapUrl(), '_blank')}
        />
      )}
      
      <ChatInterface 
        isOpen={showChatInterface} 
        onClose={handleCloseChat} 
      />
      
      <VoiceInterface 
        isOpen={showVoiceInterface} 
        onClose={handleCloseVoice} 
      />
    </div>
  );
};

export default Chatbot;
